import { useCallback, useState } from 'react';

/** Remembers the panel's collapsed choice across reloads. */
const MINIMIZED_KEY = 'tappeer.ai-chat.panel-minimized';

/** Survives a reload so a greeting streamed into a closed panel is not lost. */
const UNREAD_KEY = 'tappeer.ai-chat.unread';

/**
 * Suffixes the key with the user so a shared browser or an account switch never
 * inherits the previous user's badge. Mirrors the greeting marker's scoping.
 */
function scoped(key: string, userId: string | null): string {
  return userId === null ? key : `${key}.${userId}`;
}

function read(key: string, fallback: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(key);
    return stored === null ? fallback : stored === 'true';
  } catch {
    // Private mode / disabled storage — fall back to the default.
    return fallback;
  }
}

function write(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Nothing to do — the state still applies for this session.
  }
}

interface ChatPanelState {
  /** True while the side panel is collapsed to its header bar. */
  readonly minimized: boolean;
  /** Persists the choice; opening the panel also dismisses the badge. */
  readonly setMinimized: (minimized: boolean) => void;
  /** True when the assistant spoke while the panel was closed. */
  readonly unread: boolean;
  readonly markUnread: () => void;
  readonly clearUnread: () => void;
}

/**
 * Persisted collapsed/unread state of the AI side panel. Starts collapsed so a
 * greeting arrives as a badge instead of opening the panel unprompted.
 * @param userId Scopes the stored state; null until the session is known.
 */
export function useChatPanelState(userId: string | null = null): ChatPanelState {
  const minimizedKey = scoped(MINIMIZED_KEY, userId);
  const unreadKey = scoped(UNREAD_KEY, userId);

  const [minimized, setMinimizedState] = useState(() => read(minimizedKey, true));
  const [unread, setUnreadState] = useState(() => read(unreadKey, false));

  const clearUnread = useCallback(() => {
    setUnreadState(false);
    write(unreadKey, false);
  }, [unreadKey]);

  const markUnread = useCallback(() => {
    setUnreadState(true);
    write(unreadKey, true);
  }, [unreadKey]);

  const setMinimized = useCallback(
    (next: boolean) => {
      setMinimizedState(next);
      write(minimizedKey, next);
      // Opening the chat is what counts as reading it.
      if (!next) clearUnread();
    },
    [minimizedKey, clearUnread]
  );

  return { minimized, setMinimized, unread, markUnread, clearUnread };
}
